import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Gallery, Image } from '@anwb/poncho/components/gallery';
import ImageComponent from '@anwb/poncho/components/image';
import { Theme } from '@anwb/poncho/design-tokens/theme';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { useTheme } from 'styled-components';
import { LeaseCarDataResponseOccasion } from 'types';
import { staticAssetsUrl } from 'utils/constants';
import { trackEvent } from 'utils/trackEvent';

import {
	isFlexSelector,
	isNewCarSelector,
	leaseCarDataNameSelector,
	stateSelector,
} from '../../../../../store/selectors';

import { getOccasionImages } from './utils/getOccasionImages';

import { StyledSpinner } from './styles/occasionGallery.styled';

export const PhotoGallery = () => {
	const store = useSelector(stateSelector);
	const isNewCar = useSelector(isNewCarSelector);
	const isFlex = useSelector(isFlexSelector);

	const { leaseCarData } = store;
	const [occasionImages, setOccasionImages] = useState<Image[]>([]);
	const [isLoadingImages, setIsLoadingImages] = useState(false);
	const leaseCarApiCarName = useSelector(leaseCarDataNameSelector);
	const gaFunnelInfo = useGaFunnelInfo();
	const theme: Theme = useTheme() as Theme;

	const PhotosNotAvailableW279 = new URL(
		'/images/error-no-images-available-w279.svg',
		staticAssetsUrl()
	).href;
	const PhotosNotAvailableW754 = new URL(
		'/images/error-no-images-available-w754.svg',
		staticAssetsUrl()
	).href;

	const selectImage = useCallback(
		() =>
			window.innerWidth > parseInt(theme.viewportBreakpoint.sm, 10)
				? PhotosNotAvailableW754
				: PhotosNotAvailableW279,

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[theme.viewportBreakpoint.sm]
	);

	const [fallbackImage, setFallbackImage] = useState(selectImage());

	useEffect(() => {
		const setImageBasedOnViewportSize = () => {
			setFallbackImage(selectImage());
		};

		window.addEventListener('resize', setImageBasedOnViewportSize);
		return () => window.removeEventListener('resize', setImageBasedOnViewportSize);
	}, [selectImage]);

	useEffect(() => {
		const fetchOccasionImages = async () => {
			if (leaseCarData && !isNewCar) {
				setIsLoadingImages(true);
				try {
					const galleryImages = await getOccasionImages(
						(leaseCarData as LeaseCarDataResponseOccasion).vehicleMediaData,
						leaseCarApiCarName
					);
					setOccasionImages(galleryImages);
				} finally {
					setIsLoadingImages(false);
				}
			}
		};

		fetchOccasionImages();
	}, [isNewCar, leaseCarApiCarName, leaseCarData]);

	const trackGalleryEvent = (activeSlide: number, direction: 'previous' | 'next') => {
		trackEvent({
			event: 'user_interaction',
			type: 'slider',
			component: isFlex ? 'flex-gallery' : 'occasion-gallery',
			action: direction,
			status: `${activeSlide + 1}/${occasionImages.length}`,
			...gaFunnelInfo,
		});
	};

	if (isNewCar) {
		return null;
	}

	if (isLoadingImages) {
		return <StyledSpinner />;
	}

	return occasionImages.length > 0 ? (
		<Gallery
			reference="occasion-gallery"
			slides={occasionImages}
			variant="blue_square"
			onChangeSlideHandler={trackGalleryEvent}
		/>
	) : (
		<ImageComponent src={fallbackImage} alt="Foto's nog niet beschikbaar" />
	);
};
